/**
 * 项目孵化器面板（扩展⑦）：成队前后都可用——先定方向，再谈执行。
 * - 方向室：成员各写一句 → 后端归一（LLM/规则，via 标注）→ 确定性并查集聚类 → 一人一票 → 队长选定
 *   （选定动作同事务落 append-only 决策日志，「上次定了什么」永不脱节）；
 * - 项目沙盘：拖动投入/契合度/时间/风险滑块，shared 的确定性公式实时给出完成指数与建议
 *   （模拟分，不是概率预测——口径声明常驻页面）。
 */
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { describeSimulatorFormula, simulateOutcome } from '@crossmatch/shared';

import { incubatorApi } from '../api/incubator.js';
import { toUserMessage } from '../api/errors.js';
import type { TeamDto } from '../api/types.js';
import { Button } from './Button.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { ErrorState } from './ErrorState.js';
import { Loading } from './Loading.js';
import { ScoreBar } from './ScoreBar.js';
import { Tag } from './Tag.js';
import { skillLabel } from '../lib/format.js';
import { useTeamIdeasQuery } from '../hooks/queries.js';
import { qk } from '../lib/queryKeys.js';
import { toast } from '../store/toast.js';

const RISK_LABELS = { low: '低', mid: '中', high: '高' } as const;
const RISK_TONES = { low: 'ok', mid: 'warn', high: 'danger' } as const;

export function IncubatorPanel({ team, meId }: { team: TeamDto; meId: string }) {
  const queryClient = useQueryClient();
  const ideasQuery = useTeamIdeasQuery(team.id);
  const [draft, setDraft] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pendingChoose, setPendingChoose] = useState<string | null>(null);

  // ---- 沙盘状态（默认值取队伍实际：已确认人数；公式在 shared，前端只传参） ----
  const acceptedCount = team.members.filter((member) => member.status === 'accepted').length;
  const [hours, setHours] = useState(8);
  const [size, setSize] = useState(Math.max(acceptedCount, 1));
  const [skillMatch, setSkillMatch] = useState(60);
  const [days, setDays] = useState(30);
  const [riskExam, setRiskExam] = useState(false);
  const [riskLeave, setRiskLeave] = useState(false);
  const [riskPivot, setRiskPivot] = useState(false);

  const sim = useMemo(
    () =>
      simulateOutcome({
        weeklyHoursPerMember: hours,
        teamSize: size,
        skillMatchPct: skillMatch,
        daysToDeadline: days,
        riskExamWeek: riskExam,
        riskMemberLeave: riskLeave,
        riskPivot: riskPivot,
      }),
    [hours, size, skillMatch, days, riskExam, riskLeave, riskPivot],
  );

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: qk.teamIdeas(team.id) });
    void queryClient.invalidateQueries({ queryKey: qk.myTeams() });
    void queryClient.invalidateQueries({ queryKey: qk.teamDecisions(team.id) });
  };

  const submit = useMutation({
    mutationFn: (rawText: string) => incubatorApi.submit(team.id, rawText),
    onSuccess: (data) => {
      invalidate();
      setDraft('');
      setSubmitError(null);
      if (data.idea.via === 'llm') {
        toast.success(`方向「${data.idea.label}」已收录`, '本条由大模型归纳标签与关键词');
      } else {
        toast.success(`方向「${data.idea.label}」已收录`, '规则解析（未配置 LLM 或调用失败时自动回退，功能不受影响）');
      }
    },
    onError: (error) => setSubmitError(`提交方向失败：${toUserMessage(error)}`),
  });

  const vote = useMutation({
    mutationFn: (clusterKey: string) => incubatorApi.vote(team.id, clusterKey),
    onSuccess: invalidate,
  });
  const unvote = useMutation({
    mutationFn: (clusterKey: string) => incubatorApi.unvote(team.id, clusterKey),
    onSuccess: invalidate,
  });
  const choose = useMutation({
    mutationFn: (clusterKey: string) => incubatorApi.choose(team.id, clusterKey),
    onSuccess: (data) => {
      setPendingChoose(null);
      invalidate();
      toast.success(`项目方向已确定为「${data.direction.label}」`, '已同步写入决策日志，全队可见');
    },
    onError: (error) => {
      setPendingChoose(null);
      toast.error('选定方向失败', toUserMessage(error));
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const text = draft.trim();
    if (text.length < 5) {
      setSubmitError('提交方向失败：至少写 5 个字（做什么、给谁用）');
      return;
    }
    setSubmitError(null);
    submit.mutate(text);
  };

  const isOwner = team.ownerId === meId;
  const view = ideasQuery.data ?? null;
  const clusters = view?.clusters ?? [];
  const ideaById = useMemo(() => new Map((view?.ideas ?? []).map((idea) => [idea.id, idea])), [view]);

  const chooseTarget = pendingChoose !== null ? clusters.find((cluster) => cluster.key === pendingChoose) : undefined;

  return (
    <div className="stack">
      <section className="stack-sm" aria-label="方向室">
        <div className="row-tight">
          <span className="card-sub">方向室（各写一句，系统自动归类，一人一票定方向）</span>
          {view?.direction ? (
            <Tag tone="ok" title={`由 ${view.direction.chosenByName} 选定 · 聚 ${view.direction.ideaCount} 条想法`}>
              当前方向：{view.direction.label}
            </Tag>
          ) : null}
        </div>

        <form className="stack-sm" onSubmit={handleSubmit} noValidate>
          {submitError !== null ? (
            <div className="banner banner-danger" role="alert">
              <span className="banner-ico" aria-hidden="true">
                !
              </span>
              <span className="wrap-anywhere">{submitError}</span>
            </div>
          ) : null}
          <div className="grid-2">
            <div className="field">
              <label className="field-label" htmlFor={`idea-draft-${team.id}`}>
                一句话方向（5–200 字）
              </label>
              <textarea
                id={`idea-draft-${team.id}`}
                className="textarea"
                value={draft}
                maxLength={200}
                placeholder="如：想做校园二手书循环平台，前端 React，能两个人周末线下碰头"
                onChange={(event) => setDraft(event.target.value)}
              />
            </div>
            <div className="field">
              <span className="field-label">方向候选（自动聚类）</span>
              {ideasQuery.isPending ? (
                <Loading label="正在读取方向室…" rows={2} />
              ) : ideasQuery.isError ? (
                <ErrorState error={ideasQuery.error} title="读取方向室失败" onRetry={() => void ideasQuery.refetch()} />
              ) : clusters.length === 0 ? (
                <p className="muted small">还没有方向提议。每人写一句"想做什么"，相似的提议会自动聚成候选。</p>
              ) : (
                <div className="stack-sm">
                  {clusters.map((cluster) => (
                    <div className="card" key={cluster.key}>
                      <div className="row-tight">
                        <strong>{cluster.label}</strong>
                        <Tag tone="muted">聚 {cluster.ideaIds.length} 条</Tag>
                        <Tag tone="primary">{cluster.votes.length} 票</Tag>
                        {cluster.skills.map((skill) => (
                          <Tag key={skill} tone="school">
                            {skillLabel(skill)}
                          </Tag>
                        ))}
                      </div>
                      <div className="stack-xs">
                        {cluster.ideaIds.map((ideaId) => {
                          const idea = ideaById.get(ideaId);
                          if (idea === undefined) return null;
                          return (
                            <div className="row-tight" key={ideaId}>
                              <span className="small wrap-anywhere">「{idea.rawText}」</span>
                              <span className="muted small">
                                {idea.authorName} · {idea.via === 'llm' ? 'AI 归纳' : '规则归纳'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="row-tight">
                        {cluster.myVoted ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={unvote.isPending && unvote.variables === cluster.key}
                            onClick={() => unvote.mutate(cluster.key)}
                          >
                            撤回投票
                          </Button>
                        ) : (
                          <Button
                            variant="secondary"
                            size="sm"
                            loading={vote.isPending && vote.variables === cluster.key}
                            onClick={() => vote.mutate(cluster.key)}
                          >
                            投这个方向
                          </Button>
                        )}
                        {isOwner ? (
                          <Button variant="primary" size="sm" onClick={() => setPendingChoose(cluster.key)}>
                            选定为方向
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="row">
            <Button type="submit" variant="secondary" size="sm" loading={submit.isPending}>
              {submit.isPending ? '正在归纳…' : '提交方向'}
            </Button>
          </div>
        </form>
      </section>

      <hr className="divider" />

      <section className="stack-sm" aria-label="项目沙盘">
        <div className="row-tight">
          <span className="card-sub">项目沙盘（拖动滑块预演备赛节奏）</span>
          <Tag tone={RISK_TONES[sim.riskLevel]}>风险：{RISK_LABELS[sim.riskLevel]}</Tag>
        </div>
        <div className="grid-2">
          <div className="stack-sm">
            <div className="field">
              <label className="field-label" htmlFor={`sim-hours-${team.id}`}>
                每人每周投入：{hours} 小时
              </label>
              <input
                id={`sim-hours-${team.id}`}
                type="range"
                min={0}
                max={40}
                value={hours}
                onChange={(event) => setHours(Number(event.target.value))}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor={`sim-size-${team.id}`}>
                队伍人数：{size} 人
              </label>
              <input
                id={`sim-size-${team.id}`}
                type="range"
                min={1}
                max={8}
                value={size}
                onChange={(event) => setSize(Number(event.target.value))}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor={`sim-skill-${team.id}`}>
                方向与技能契合度：{skillMatch}%（可参考「缺口雷达」的已覆盖比例）
              </label>
              <input
                id={`sim-skill-${team.id}`}
                type="range"
                min={0}
                max={100}
                step={5}
                value={skillMatch}
                onChange={(event) => setSkillMatch(Number(event.target.value))}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor={`sim-days-${team.id}`}>
                距下一个赛事节点：{days} 天
              </label>
              <input
                id={`sim-days-${team.id}`}
                type="range"
                min={7}
                max={90}
                value={days}
                onChange={(event) => setDays(Number(event.target.value))}
              />
            </div>
            <div className="row-tight">
              <label className="checkbox">
                <input type="checkbox" checked={riskExam} onChange={(event) => setRiskExam(event.target.checked)} />
                考试周（产能 ×0.85）
              </label>
              <label className="checkbox">
                <input type="checkbox" checked={riskLeave} onChange={(event) => setRiskLeave(event.target.checked)} />
                成员可能退出（×0.80）
              </label>
              <label className="checkbox">
                <input type="checkbox" checked={riskPivot} onChange={(event) => setRiskPivot(event.target.checked)} />
                中途换方向（×0.75）
              </label>
            </div>
          </div>
          <div className="stack-sm">
            <ScoreBar label="完成指数" value={sim.readinessPct / 100} hint={`产能 ${sim.capacityUnits} / 需求 ${sim.neededUnits}（人时）`} />
            <span className="card-sub">沙盘建议（按当前最紧约束）</span>
            {sim.advice.map((line) => (
              <p className="small wrap-anywhere" key={line}>
                · {line}
              </p>
            ))}
            <p className="chart-note">口径声明：这是模拟分，不是概率预测。{describeSimulatorFormula()}</p>
          </div>
        </div>
      </section>

      <ConfirmDialog
        open={pendingChoose !== null}
        title="选定项目方向？"
        description={
          chooseTarget ? (
            <>
              <p>
                将把「{chooseTarget.label}」确定为队伍项目方向（聚 {chooseTarget.ideaIds.length} 条想法，
                当前 {chooseTarget.votes.length} 票）。
              </p>
              <p className="muted small">此操作会写入决策日志（不可篡改），全队立即可见。</p>
            </>
          ) : null
        }
        confirmText="确定方向"
        pending={choose.isPending}
        onCancel={() => setPendingChoose(null)}
        onConfirm={() => {
          if (pendingChoose !== null) choose.mutate(pendingChoose);
        }}
      />
    </div>
  );
}
